import { useCallback, useEffect, useRef, useState } from 'react';

import type { BillingStatus, PlanName } from '../../../api/types';

// PARA-131 (AD-4, AC-10) — стратегия разрешения рассинхрона между колбэком
// платёжного шита и асинхронной активацией подписки через webhook.
// Значения именованные: литеральных таймаутов по коду быть не должно.
export const POLLING_INTERVAL_MS = 2000;
export const POLLING_WINDOW_MS = 30000;

/** Состояние ожидания активации. Живёт ТОЛЬКО в памяти компонента (AC-E5). */
export type ActivationPhase = 'idle' | 'paid' | 'pending' | 'timeout';

/** Фаза, с которой начинается ожидание: AC-6 (paid) либо AC-7 (pending). */
export type ActivationStartPhase = 'paid' | 'pending';

export interface ActivationPolling {
  phase: ActivationPhase;
  /** Оплаченный тариф — нужен для текстов AC-14a и для условия успеха. */
  plan: PlanName | null;
  /** Значение для useBillingStatus: интервал активен только во время ожидания. */
  refetchInterval: number | false;
  /** Снять `expires_at` ДО отправки POST /billing/subscriptions (AC-10). */
  snapshot: (expiresAt: string | null) => void;
  start: (plan: PlanName, phase: ActivationStartPhase) => void;
  /** Сверить очередной ответ GET /billing/me с условием успеха. */
  observe: (status: BillingStatus | undefined) => void;
  reset: () => void;
}

interface WaitingState {
  phase: ActivationPhase;
  plan: PlanName | null;
  /** Счётчик запусков: перезапуск ожидания обязан перезапустить окно. */
  attempt: number;
}

const IDLE: WaitingState = { phase: 'idle', plan: null, attempt: 0 };

/** AC-10: успех требует ВСЕХ трёх условий. Совпадения тарифа мало — у того,
 * кто оплачивает уже действующий тариф, оно выполняется ещё до webhook'а, и
 * поллинг «успешно» показал бы старую дату как новую. */
function isActivated(
  status: BillingStatus,
  plan: PlanName | null,
  expiresAtBefore: string | null,
): boolean {
  return (
    status.plan === plan && status.expires_at !== null && status.expires_at !== expiresAtBefore
  );
}

export function useActivationPolling(onActivated: () => void): ActivationPolling {
  const [state, setState] = useState<WaitingState>(IDLE);
  const expiresAtBeforeRef = useRef<string | null>(null);

  const isWaiting = state.phase === 'paid' || state.phase === 'pending';

  // Окно ожидания: по его истечении — AC-E3 (нетревожное «обрабатывается»),
  // а не ошибка оплаты. Возврат из эффекта снимает таймер при размонтировании,
  // поэтому в фоне ожидание не продолжается.
  useEffect(() => {
    if (!isWaiting) {
      return;
    }
    const timer = setTimeout(() => {
      setState((previous) => ({ ...previous, phase: 'timeout' }));
    }, POLLING_WINDOW_MS);
    return () => clearTimeout(timer);
  }, [isWaiting, state.attempt]);

  const snapshot = useCallback((expiresAt: string | null) => {
    expiresAtBeforeRef.current = expiresAt;
  }, []);

  const start = useCallback((plan: PlanName, phase: ActivationStartPhase) => {
    setState((previous) => ({ phase, plan, attempt: previous.attempt + 1 }));
  }, []);

  const reset = useCallback(() => {
    setState(IDLE);
  }, []);

  const observe = useCallback(
    (status: BillingStatus | undefined) => {
      // 'timeout' тоже сверяется: подтверждение может прийти после окна, и
      // ручное «Обновить статус» обязано увести экран из состояния ожидания.
      if (state.phase === 'idle' || status === undefined) {
        return;
      }
      if (isActivated(status, state.plan, expiresAtBeforeRef.current)) {
        setState(IDLE);
        // AC-6: снятие лимита меняет поведение экрана задач.
        onActivated();
      }
    },
    [onActivated, state.phase, state.plan],
  );

  return {
    phase: state.phase,
    plan: state.plan,
    refetchInterval: isWaiting ? POLLING_INTERVAL_MS : false,
    snapshot,
    start,
    observe,
    reset,
  };
}
